import classNames from "classnames";
import type { ButtonProps } from "../ui/Button/Button";
import Button from "../ui/Button/Button";
import { ArrowLeft, ChevronRight } from "../ui/Icon/Icon";
import styles from "./Page.module.scss";
import { Link } from "react-router-dom";
import { Fragment, useEffect, useLayoutEffect, useState } from "react";
import Loader from "../ui/Loader/Loader";
import Dropdown, { DropdownProps } from "../ui/Dropdown/Dropdown";
import Popover, { PopoverProps } from "../ui/Popover/Popover";

interface PageProps {
  onBack?: () => void | Promise<void>;
  children?: React.ReactNode;
  title: React.ReactNode;
  documentTitle: string;
  buttons?: (
    | (ButtonProps & { type: "button" })
    | (DropdownProps & { type: "dropdown" })
    | (PopoverProps & { type: "popover" })
  )[];
  className?: string;
  fullHeight?: boolean;
  loading?: boolean;
  breadcrumbs?: { name: React.ReactNode; url: string }[];
  showHeader?: boolean;
}

function Page({
  onBack,
  title,
  documentTitle,
  children,
  buttons,
  className,
  fullHeight,
  loading,
  breadcrumbs,
  showHeader = true,
}: PageProps) {
  const [breadcrumbsRef, setBreadcrumbsRef] = useState<HTMLDivElement | null>(
    null,
  );
  const [displayedBreadcrumbs, setDisplayedBreadcrumbs] = useState<
    { name: React.ReactNode; url: string }[] | undefined
  >(breadcrumbs);
  const [breadcrumbsInitialised, setBreadcrumbsInitialised] = useState(
    !breadcrumbs || breadcrumbs.length <= 1,
  );

  useEffect(() => {
    document.title = `${documentTitle} | Macaw`;
  }, [documentTitle]);

  useEffect(() => {
    setDisplayedBreadcrumbs(breadcrumbs);
  }, [breadcrumbs]);

  useLayoutEffect(() => {
    const onResize = () => {
      if (breadcrumbsRef) {
        if (breadcrumbsRef.scrollWidth > breadcrumbsRef.clientWidth) {
          setDisplayedBreadcrumbs((previous) => [
            ...(previous?.slice(1) ?? []),
          ]);
        } else {
          setBreadcrumbsInitialised(true);
        }
      }
    };

    const resizeObserver = new ResizeObserver(onResize);

    if (breadcrumbsRef) {
      resizeObserver.observe(breadcrumbsRef);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [breadcrumbs, displayedBreadcrumbs, breadcrumbsRef]);

  return (
    <div
      className={classNames(styles.page, className, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      {showHeader && (
        <div className={styles.header}>
          {onBack ? (
            <Button
              className={styles.backButton}
              onClick={onBack}
              variant="outline"
              size="small"
              aria-label="Back"
            >
              <ArrowLeft size="small" />
            </Button>
          ) : null}
          <div
            className={classNames(styles.breadcrumbs, {
              [styles.breadcrumbsInitialised]: breadcrumbsInitialised,
            })}
            ref={setBreadcrumbsRef}
          >
            {breadcrumbs &&
              displayedBreadcrumbs &&
              breadcrumbs.length > displayedBreadcrumbs.length && (
                <Fragment
                  key={
                    breadcrumbs[
                      breadcrumbs.length - displayedBreadcrumbs.length - 1
                    ].url
                  }
                >
                  <Link
                    className={styles.breadcrumb}
                    to={
                      breadcrumbs[
                        breadcrumbs.length - displayedBreadcrumbs.length - 1
                      ].url
                    }
                  >
                    ...
                  </Link>
                  <ChevronRight size="small" className={styles.separator} />
                </Fragment>
              )}
            {displayedBreadcrumbs?.map((breadcrumb) => (
              <Fragment key={breadcrumb.url}>
                <Link className={styles.breadcrumb} to={breadcrumb.url}>
                  {breadcrumb.name}
                </Link>
                <ChevronRight size="small" className={styles.separator} />
              </Fragment>
            ))}
            <h1
              className={classNames(styles.title, {
                [styles.titleTruncate]: displayedBreadcrumbs?.length === 0,
              })}
            >
              {title}
            </h1>
          </div>
          <div className={styles.headerButtons} id="header-buttons">
            {!!buttons?.length &&
              buttons.map((button, index) => {
                if (button.type === "popover") {
                  return <Popover {...button} key={index} />;
                }

                if (button.type === "button") {
                  return <Button {...button} key={index} />;
                }

                return <Dropdown {...button} key={index} />;
              })}
          </div>
        </div>
      )}
      {loading ? (
        <>
          <div className={styles.loading}>
            <Loader />
          </div>
          <div style={{ display: "none" }}>{children}</div>
        </>
      ) : (
        children
      )}
    </div>
  );
}

export default Page;
